import { uuidv4 } from "../utils/uuidv4";

// // //

if (!("onscrollend" in window)) {
  const scrollEndEvent = new Event("scrollend");
  const touchIdentifiers = new Set<number>();

  document.addEventListener(
    "touchstart",
    (event: TouchEvent) => {
      for (const touch of Array.from(event.changedTouches)) {
        touchIdentifiers.add(touch.identifier);
      }
    },
    { passive: true }
  );

  document.addEventListener(
    "touchend",
    (event: TouchEvent) => {
      for (const touch of Array.from(event.changedTouches)) {
        touchIdentifiers.delete(touch.identifier);
      }
    },
    { passive: true }
  );

  interface ScrollEndPolyfillData {
    scrollListener: () => void; // Changed from (event: Event) => void as event wasn't used
    listeners: number;
    timeoutId?: number;
  }

  const scrollEndDataMap = new WeakMap<EventTarget, ScrollEndPolyfillData>();

  type OriginalAddEventListener = (
    type: string,
    listener: EventListenerOrEventListenerObject | null,
    options?: boolean | AddEventListenerOptions
  ) => void;

  type OriginalRemoveEventListener = (
    type: string,
    listener: EventListenerOrEventListenerObject | null,
    options?: boolean | EventListenerOptions
  ) => void;

  function patchMethod(
    target: any, // Element.prototype, Window, Document
    methodName: "addEventListener" | "removeEventListener",
    overrideCallback: (
      this: EventTarget,
      originalMethod: Function, // Actually OriginalAddEventListener or OriginalRemoveEventListener
      type: string,
      listener?: EventListenerOrEventListenerObject | null,
      options?: boolean | AddEventListenerOptions | EventListenerOptions
    ) => void
  ): void {
    const originalMethod = target[methodName] as Function;
    target[methodName] = function (
      this: EventTarget,
      type: string,
      listener?: EventListenerOrEventListenerObject | null,
      options?: boolean | AddEventListenerOptions | EventListenerOptions
    ): void {
      originalMethod.call(this, type, listener, options);
      overrideCallback.call(this, originalMethod, type, listener, options);
    };
  }

  function addEventListenerOverride(
    this: EventTarget,
    originalMethod: OriginalAddEventListener,
    type: string,
    _listenerBeingAdded?: EventListenerOrEventListenerObject | null, // Not directly used by polyfill's internal logic for its own listener
    optionsForListener?: boolean | AddEventListenerOptions
  ): void {
    if (type !== "scroll" && type !== "scrollend") {
      return;
    }

    let polyfillData = scrollEndDataMap.get(this);

    if (polyfillData === undefined) {
      const self = this; // Capture 'this' for use in scrollListener
      polyfillData = {
        scrollListener: () => {
          const currentPolyfillData = scrollEndDataMap.get(self);
          if (!currentPolyfillData) return; // Target might have been cleaned up

          if (currentPolyfillData.timeoutId !== undefined) {
            clearTimeout(currentPolyfillData.timeoutId);
          }
          currentPolyfillData.timeoutId = window.setTimeout(() => {
            // Check again if data exists, as it might be cleared by removeEventListener
            const latestPolyfillData = scrollEndDataMap.get(self);
            if (!latestPolyfillData) return;

            if (touchIdentifiers.size > 0) {
              if (latestPolyfillData.scrollListener) {
                latestPolyfillData.timeoutId = window.setTimeout(latestPolyfillData.scrollListener, 100);
              }
            } else {
              self.dispatchEvent(scrollEndEvent);
              latestPolyfillData.timeoutId = undefined;
            }
          }, 100);
        },
        listeners: 0,
      };
      originalMethod.call(this, "scroll", polyfillData.scrollListener, optionsForListener);
      scrollEndDataMap.set(this, polyfillData);
    }
    polyfillData.listeners++;
  }

  function removeEventListenerOverride(
    this: EventTarget,
    originalMethod: OriginalRemoveEventListener,
    type: string,
    _listenerBeingRemoved?: EventListenerOrEventListenerObject | null, // Not directly used
    optionsForListener?: boolean | EventListenerOptions
  ): void {
    if (type !== "scroll" && type !== "scrollend") {
      return;
    }

    const polyfillData = scrollEndDataMap.get(this);
    if (polyfillData !== undefined) {
      polyfillData.listeners--;

      if (polyfillData.listeners <= 0) {
        if (polyfillData.timeoutId !== undefined) {
          clearTimeout(polyfillData.timeoutId);
        }
        originalMethod.call(this, "scroll", polyfillData.scrollListener, optionsForListener);
        scrollEndDataMap.delete(this);
      }
    }
  }

  patchMethod(Element.prototype, "addEventListener", addEventListenerOverride as any); // Cast due to broad 'Function' in patchMethod
  patchMethod(window, "addEventListener", addEventListenerOverride as any);
  patchMethod(document, "addEventListener", addEventListenerOverride as any);

  patchMethod(Element.prototype, "removeEventListener", removeEventListenerOverride as any);
  patchMethod(window, "removeEventListener", removeEventListenerOverride as any);
  patchMethod(document, "removeEventListener", removeEventListenerOverride as any);
}

// // //
 
const SVG_ARROW_RIGHT = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
<path d="M5 12L19 12M19 12L12 5M19 12L12 19" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`;

class Carousel {
  elements: {
    root: HTMLElement;
    scroller: HTMLElement;
    slides: NodeListOf<HTMLElement>;
    previous: HTMLElement | null;
    next: HTMLElement | null;
    controlsContainer: HTMLElement | null;
    pagination: HTMLElement | null;
    scrollbarTrack: HTMLElement | null;
    scrollbarProgress: HTMLElement | null;
    scrollbarThumb: HTMLElement | null;
    scrollbarContainer: HTMLElement | null;
  };
  hasIntersected: Set<IntersectionObserverEntry>;
  scrollStartX: number | undefined;
  scrollThumbPosition: number | undefined;
  isMouseDown: boolean;
  spaceBetween: number;
  slideWidth: number;
  hasCustomScrollbar: boolean;
  carouselMaxScroll: number;
  current: HTMLElement | null;
  carousel_observer: IntersectionObserver;

  constructor(element: HTMLElement) {
    this.elements = {
      root: element,
      scroller: element.querySelector("[data-n-carousel-scroller]"),
      slides: element.querySelectorAll("[data-n-carousel-slide]"),
      previous: null,
      next: null,
      controlsContainer: null,
      pagination: null,
      scrollbarTrack: null,
      scrollbarProgress: null,
      scrollbarThumb: null,
      scrollbarContainer: null,
    };

    this.hasIntersected = new Set();

    this.elements.scroller.classList.add(
      "n-carousel__scroller",
      "n-carousel__scroller--snap-slide"
    );

    this.scrollStartX = undefined;
    this.scrollThumbPosition = undefined;
    this.isMouseDown = false;

    this.spaceBetween = parseInt(
      getComputedStyle(this.elements.scroller)["gap"]
    );

    this.slideWidth = this.elements.slides[0].clientWidth;

    this.elements.root.setAttribute("tabindex", "-1");

    this.elements.scroller.setAttribute("id", `scroller_${uuidv4()}`);
    this.elements.scroller.setAttribute("role", "group");
    this.elements.scroller.setAttribute("aria-live", "Polite");

    this.hasCustomScrollbar =
      this.elements.root.getAttribute("data-carousel-scrollbar") !== "none";
    if (this.hasCustomScrollbar) {
      this.createCustomScrollbar();
    }

    this.createObservers();
    this.createPagination();
    this.createControls();
    this.initializeState();
    this.subscribe();
    this.synchronize();

    this.carouselMaxScroll =
      this.elements.scroller.scrollWidth - this.elements.scroller.clientWidth;
  }

  private synchronize = () => {
    setTimeout(() => {
      for (let observation of this.hasIntersected) {
        observation.target.toggleAttribute(
          "inert",
          !observation.isIntersecting
        );

        const dot =
          this.elements.pagination.children[
            this.getElementIndex(observation.target)
          ];

        dot.setAttribute(
          "aria-selected",
          observation.isIntersecting.toString()
        );
        dot.setAttribute("tabindex", !observation.isIntersecting ? "-1" : "0");

        if (observation.isIntersecting) {
          this.current = observation.target as HTMLElement;
        }
      }
      this.updateControls();
      this.hasIntersected.clear();
    }, 100);
  };

  private createObservers = () => {
    this.carousel_observer = new IntersectionObserver(
      (observations) => {
        for (let observation of observations) {
          this.hasIntersected.add(observation);

          observation.target.classList.toggle(
            "--in-view",
            observation.isIntersecting
          );
          observation.target.classList.toggle(
            "--out-of-view",
            !observation.isIntersecting
          );
        }
      },
      {
        root: this.elements.scroller,
        threshold: 0.6,
      }
    );
  };

  private initializeState = () => {
    this.current = this.elements.slides[0];

    this.elements.slides.forEach((slide, index) => {
      slide.id = `slide_${uuidv4()}`;
      slide.classList.add("n-carousel__slide");

      slide.setAttribute(
        "aria-label",
        `${index + 1} / ${this.elements.slides.length}`
      );

      this.elements.pagination.appendChild(this.createMarker(slide, index));
    });
  };

  goNext = () => {
    const isAtEnd = this.elements.scroller.scrollLeft >= this.carouselMaxScroll;
    if (isAtEnd) {
      return;
    }
    this.slideCarousel({ direction: 1 });
  };

  goPrevious = () => {
    const isAtStart = this.elements.scroller.scrollLeft <= 0;
    if (isAtStart) {
      return;
    }
    this.slideCarousel({ direction: -1 });
  };

  slideCarousel = ({ direction }: { direction: number }) => {
    const slidesPerView = this.getSlidesPerView();

    const amountToScroll =
      (this.elements.slides[0].clientWidth + this.spaceBetween) * slidesPerView;

    const pos = Math.floor(amountToScroll * direction);

    this.elements.scroller.scrollBy({ left: pos, top: 0, behavior: "smooth" });
  };

  goToSlide = (element: HTMLElement) => {
    const scrollport = this.elements.scroller;

    const delta = Math.abs(
      scrollport.offsetLeft - element.offsetLeft + this.spaceBetween
    );
    const scrollerPadding = parseInt(
      getComputedStyle(scrollport)["padding-left"]
    );

    const pos = delta - scrollerPadding;

    scrollport.scrollTo({ left: pos, top: 0, behavior: "smooth" });
  };

  private handlePaginate = (e: Event) => {
    const target = e.target as HTMLElement;
    if (target.classList.contains("n-carousel__pagination")) return;

    target.setAttribute("aria-selected", "true");
    const element = this.elements.slides[this.getElementIndex(target)];

    this.goToSlide(element);
  };

  private createPagination = () => {
    let nav = document.createElement("nav");
    nav.className = "n-carousel__pagination";
    nav.setAttribute("role", "tablist");
    this.elements.scroller.nextElementSibling.prepend(nav);

    this.elements.pagination = nav;
  };

  private createMarker = (slide: HTMLElement, index: number) => {
    index++;
    return this.createMarkerDot({ index, slide });
  };

  private createMarkerDot = ({
    index,
    slide,
  }: {
    index: number;
    slide: HTMLElement;
  }) => {
    const marker = document.createElement("button");
    const title = slide.querySelector("h1, h2, h3") as HTMLElement;
    marker.className = "n-carousel__pagination-dot";
    marker.type = "button";
    marker.role = "tab";
    marker.title = title?.innerText ?? "";
    marker.setAttribute("aria-setsize", this.elements.slides.length.toString());
    marker.setAttribute("aria-posinset", index.toString());
    if (slide.id) {
      marker.setAttribute("aria-labelledby", slide.id);
    }
    return marker;
  };

  private subscribe = () => {
    for (let slide of Array.from(this.elements.slides)) {
      this.carousel_observer.observe(slide);
    }

    this.elements.scroller.addEventListener("scrollend", () => {
      this.synchronize();
    });
    this.elements.next.addEventListener("click", this.goNext);
    this.elements.previous.addEventListener("click", this.goPrevious);
    this.elements.root.addEventListener("keydown", this.handleKeydown);
    this.elements.pagination.addEventListener("click", this.handlePaginate);

    if (this.hasCustomScrollbar) {
      this.elements.scroller.addEventListener(
        "scroll",
        this.updateScrollbarThumbPosition
      );

      this.elements.scrollbarThumb.addEventListener(
        "pointerdown",
        this.handleScrollbarThumbMove
      );
    }
  };

  unsubscribe = () => {
    for (let slide of Array.from(this.elements.slides)) {
      this.carousel_observer.unobserve(slide);
    }

    this.elements.scroller.removeEventListener("scrollend", this.synchronize);
    this.elements.root.removeEventListener("keydown", this.handleKeydown);
    this.elements.pagination.removeEventListener("click", this.handlePaginate);

    if (this.hasCustomScrollbar) {
      this.elements.scroller.removeEventListener(
        "scroll",
        this.updateScrollbarThumbPosition
      );

      this.elements.scrollbarThumb.removeEventListener(
        "pointerdown",
        this.handleScrollbarThumbMove
      );
    }
  };

  handleKeydown = (e: KeyboardEvent) => {
    const index = this.getElementIndex(e.target as Element);

    switch (e.key) {
      case "ArrowRight":
        e.preventDefault();

        if ((e.target as HTMLElement).closest(".n-carousel__pagination")) {
          (
            this.elements.pagination.children[index + 1] as HTMLElement
          )?.focus();
        }

        this.goNext();
        break;
      case "ArrowLeft":
        e.preventDefault();

        if ((e.target as HTMLElement).closest(".n-carousel__pagination")) {
          (
            this.elements.pagination.children[index - 1] as HTMLElement
          )?.focus();
        }

        this.goPrevious();
        break;
    }
  };

  private updateControls = () => {
    const isAtStart = this.elements.scroller.scrollLeft <= 0;
    const isAtEnd = this.elements.scroller.scrollLeft >= this.carouselMaxScroll;

    if (document.activeElement === this.elements.next && isAtEnd) {
      this.elements.previous.focus();
    } else if (document.activeElement === this.elements.previous && isAtStart) {
      this.elements.next.focus();
    }

    this.elements.next.setAttribute("aria-disabled", isAtEnd.toString());
    this.elements.previous.setAttribute("aria-disabled", isAtStart.toString());
  };

  private createControls = () => {
    let controls = document.createElement("div");
    controls.className = "n-carousel__controls";

    let prevBtn = this.createControl("previous");
    let nextBtn = this.createControl("next");

    controls.appendChild(prevBtn);
    controls.appendChild(nextBtn);

    this.elements.controlsContainer = controls;
    this.elements.previous = prevBtn;
    this.elements.next = nextBtn;
    this.elements.root.prepend(controls);
  };

  private createControl = (btnType: string) => {
    const control = document.createElement("button");

    const hasTranslationAttribute = this.elements.root.hasAttribute(
      `data-carousel-${btnType}`
    );

    // if (!hasTranslationAttribute) {
    //   console.log(
    //     "No translation attibute for:",
    //     btnType,
    //     "carousel button type"
    //   );
    // }

    const translationAttribute =
      this.elements.root.getAttribute(`data-carousel-${btnType}`) || "";

    control.type = "button";
    control.title = translationAttribute;
    control.className = `n-carousel__control simple-carousel__control--${btnType} overflow-carousel-button overflow-carousel-button--${btnType}`;
    control.setAttribute("aria-label", translationAttribute);

    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.setAttribute("aria-hidden", "true");
    svg.setAttribute("viewBox", "0 0 34 34");
    svg.setAttribute("width", "34");
    svg.setAttribute("height", "34");

    svg.setAttribute("fill", "currentColor");

    let path = document.createElementNS("http://www.w3.org/2000/svg", "path");
    path.setAttribute("fill-rule", "evenodd");
    path.setAttribute("clip-rule", "evenodd");

    let arrowRightPath =
      "M4.25014 17C4.25014 16.7182 4.36208 16.448 4.56134 16.2487C4.76059 16.0495 5.03084 15.9375 5.31264 15.9375L26.123 15.9375L18.3734 8.18924C18.2747 8.09052 18.1964 7.97333 18.143 7.84435C18.0895 7.71537 18.062 7.57713 18.062 7.43752C18.062 7.29791 18.0895 7.15967 18.143 7.03069C18.1964 6.90171 18.2747 6.78452 18.3734 6.6858C18.4721 6.58708 18.5893 6.50878 18.7183 6.45535C18.8473 6.40193 18.9855 6.37443 19.1251 6.37443C19.2647 6.37443 19.403 6.40193 19.532 6.45535C19.6609 6.50878 19.7781 6.58708 19.8769 6.6858L29.4394 16.2483C29.5381 16.347 29.6165 16.4642 29.67 16.5931C29.7235 16.7221 29.751 16.8604 29.751 17C29.751 17.1396 29.7235 17.2779 29.67 17.4069C29.6165 17.5359 29.5381 17.6531 29.4394 17.7517L19.8769 27.3142C19.6775 27.5136 19.4071 27.6256 19.1251 27.6256C18.8432 27.6256 18.5728 27.5136 18.3734 27.3142C18.1741 27.1149 18.062 26.8445 18.062 26.5625C18.062 26.2806 18.1741 26.0102 18.3734 25.8108L26.123 18.0625L5.31264 18.0625C5.03084 18.0625 4.76059 17.9506 4.56134 17.7513C4.36208 17.5521 4.25014 17.2818 4.25014 17Z";

    path.setAttribute("d", arrowRightPath);

    svg.appendChild(path);
    control.appendChild(svg);

    return control;
  };

  private getScrollbarThumbWidth = () => {
    return (
      (this.elements.scroller.clientWidth /
        this.elements.scroller.scrollWidth) *
      100
    );
  };

  private createCustomScrollbar = () => {
    const scrollbarContainer = document.createElement("div");
    scrollbarContainer.className = "n-carousel__scroll-indicator";

    const scrollbarTrack = document.createElement("div");
    scrollbarTrack.className = "n-carousel__scroll-rail";
    scrollbarTrack.role = "scrollbar";
    scrollbarTrack.setAttribute("aria-orientation", "horizontal");
    scrollbarTrack.setAttribute("aria-valuemax", "100");
    scrollbarTrack.setAttribute("aria-valuemin", "0");
    scrollbarTrack.setAttribute("aria-valuenow", "0");
    scrollbarTrack.setAttribute("aria-controls", this.elements.scroller.id);
    scrollbarTrack.setAttribute("tabindex", "0");

    const scrollbarProgress = document.createElement("div");
    scrollbarProgress.className = "n-carousel__scroll-progress";
    scrollbarProgress.style.width = `0%`;

    const scrollbarThumb = document.createElement("div");
    scrollbarThumb.className = "n-carousel__scroll-dot";

    const scrollbarThumbHandle = document.createElement("div");
    scrollbarThumbHandle.className = "n-carousel__scroll-dot-handle";
    scrollbarThumbHandle.innerHTML = SVG_ARROW_RIGHT;

    scrollbarThumb.appendChild(scrollbarThumbHandle);

    scrollbarTrack.appendChild(scrollbarProgress);
    scrollbarTrack.appendChild(scrollbarThumb);
    scrollbarContainer.appendChild(scrollbarTrack);

    this.elements.scrollbarContainer = scrollbarContainer;
    this.elements.scrollbarTrack = scrollbarTrack;
    this.elements.scrollbarThumb = scrollbarThumb;
    this.elements.scrollbarProgress = scrollbarProgress;

    this.elements.scroller.nextElementSibling.prepend(scrollbarContainer);
  };

  private handleScrollbarThumbMove = (e: PointerEvent) => {
    this.elements.root.classList.add("n-carousel--grabbing");

    const storedThumbPosition = parseInt(
      this.elements.scrollbarThumb.getAttribute("data-thumb-position")
    );

    const startX = e.clientX;
    const thumbPosition =
      storedThumbPosition > 0
        ? storedThumbPosition
        : this.elements.scrollbarThumb.offsetLeft;

    this.elements.scrollbarThumb.setAttribute("aria-grabbed", "true");

    const handlePointerMove = (e: PointerEvent) => {
      const deltaX = e.clientX - startX;
      const newThumbPosition = thumbPosition + deltaX;
      const maxThumbPosition =
        this.elements.scrollbarTrack.getBoundingClientRect().width -
        this.elements.scrollbarThumb.offsetWidth;

      const boundedPosition = Math.max(
        0,
        Math.min(maxThumbPosition, newThumbPosition)
      );

      const scrollPosition =
        (boundedPosition / maxThumbPosition) * this.carouselMaxScroll;
      this.elements.scrollbarThumb.setAttribute(
        "data-thumb-position",
        boundedPosition.toString()
      );

      this.elements.scrollbarThumb.style.transform = `translate(${boundedPosition}px, -50%)`;
      this.elements.scroller.scrollTo({ left: scrollPosition, top: 0 });
    };
    const handlePointerUp = () => {
      document.removeEventListener("pointermove", handlePointerMove);
      document.removeEventListener("pointerup", handlePointerUp);
      this.elements.scrollbarThumb.removeAttribute("aria-grabbed");
      this.elements.root.classList.remove("n-carousel--grabbing");
    };
    document.addEventListener("pointermove", handlePointerMove);
    document.addEventListener("pointerup", handlePointerUp);
  };

  private updateScrollbarThumbPosition = () => {
    const scrollPosition = this.elements.scroller.scrollLeft;

    const thumbPosition =
      (scrollPosition / this.carouselMaxScroll) *
      (this.elements.scrollbarTrack.clientWidth -
        this.elements.scrollbarThumb.offsetWidth);

    const scrollPercentage =
      (scrollPosition /
        (this.elements.scroller.scrollWidth -
          this.elements.scroller.clientWidth)) *
      100;

    this.elements.scrollbarTrack.setAttribute(
      "aria-valuenow",
      scrollPercentage.toFixed(0)
    );

    this.elements.scrollbarThumb.setAttribute(
      "data-thumb-position",
      thumbPosition.toString()
    );

    this.elements.scrollbarProgress.style.width = `${scrollPercentage}%`;
    this.elements.scrollbarThumb.style.transform = `translate(${thumbPosition}px, -50%)`;
  };

  private getElementIndex = (element: Element | null) => {
    let index = 0;
    while ((element = element.previousElementSibling)) index++;
    return index;
  };

  private getSlidesPerView = () => {
    const scrollerWidth = this.elements.scroller.clientWidth; // Width of the visible area
    const slideTotalWidth =
      this.elements.slides[0].clientWidth + this.spaceBetween; // Width of a single slide including space
    return Math.floor(scrollerWidth / slideTotalWidth); // Number of slides visible
  };
}

export default Carousel;
